import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, FormLabel, FormHelperText } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Button from '@/components/CustomButtons/Button';
import FieldErrorText from '@/components/FieldErrorText/FieldErrorText';
import SftpDestinationRow from './SftpDestinationRow';
import {
  MAX_SFTP_DESTINATIONS,
  emptySftpDestination,
} from './sftpDestinations';

const labelStyle = {
  fontSize: '12px',
  color: 'grey',
};

export default function SftpDestinationList({
  destinations,
  sftpConnections,
  isDisabled,
  generateAcknowledgementFile,
  generateDailyReport,
  fileExtension,
  dateFormat,
  onChange,
  getFieldErrors,
}) {
  const { t } = useTranslation();

  const replaceAt = (index, destination, changedPath) =>
    onChange(
      destinations.map((current, i) => (i === index ? destination : current)),
      changedPath,
    );

  const removeAt = (index) =>
    onChange(
      destinations.filter((_, i) => i !== index),
      'sftp_destinations',
    );

  const add = () =>
    onChange([...destinations, emptySftpDestination()], 'sftp_destinations');

  return (
    <Box sx={{ mt: 2 }}>
      <FormLabel component='legend' style={labelStyle}>
        {t('dataMappings.sftpDestinations.title')}
      </FormLabel>
      <FormHelperText sx={{ mb: 1 }}>
        {t('dataMappings.sftpDestinations.description')}
      </FormHelperText>
      <FieldErrorText
        messages={getFieldErrors('sftp_destinations')}
        style={{ marginBottom: 8 }}
      />

      {destinations.map((destination, index) => (
        <SftpDestinationRow
          key={index}
          destination={destination}
          index={index}
          sftpConnections={sftpConnections}
          isDisabled={isDisabled}
          generateAcknowledgementFile={generateAcknowledgementFile}
          generateDailyReport={generateDailyReport}
          fileExtension={fileExtension}
          dateFormat={dateFormat}
          onChange={(next, changedPath) => replaceAt(index, next, changedPath)}
          onRemove={() => removeAt(index)}
          getFieldErrors={getFieldErrors}
        />
      ))}

      <Button
        size='sm'
        color='primary'
        simple
        onClick={add}
        disabled={isDisabled || destinations.length >= MAX_SFTP_DESTINATIONS}>
        <AddCircleOutlineIcon fontSize='small' />
        {t('dataMappings.sftpDestinations.add')}
      </Button>
    </Box>
  );
}

SftpDestinationList.propTypes = {
  destinations: PropTypes.array.isRequired,
  sftpConnections: PropTypes.array,
  isDisabled: PropTypes.bool,
  generateAcknowledgementFile: PropTypes.bool,
  generateDailyReport: PropTypes.bool,
  fileExtension: PropTypes.string,
  dateFormat: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  getFieldErrors: PropTypes.func.isRequired,
};
